import { Vector3 } from 'three';
import { Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';

/**
 * Rest-pose cotangent-Laplacian topology of a triangle mesh, stored in CSR form.
 *
 * The Laplacian `L` is the symmetric cotangent operator
 *   L_ii = Σ_{j∈N(i)} w_ij      (== `diag[i]`)
 *   L_ij = -w_ij                (off-diagonal, j ∈ N(i))
 * with edge weights `w_ij = ½(cotα + cotβ)` (½cotγ on a boundary edge). It is built
 * ONCE from the rest pose and stays constant under deformation — only the right-hand
 * side changes per solve. See {@link buildCotangentLaplacian}.
 *
 * CSR holds only the off-diagonal neighbours (the diagonal lives in `diag`):
 *   for vertex `i`, neighbours are `colIdx[rowPtr[i] .. rowPtr[i+1]]` with the matching
 *   weights `weight[...]` (the positive `w_ij`, NOT the negated off-diagonal).
 */
export interface MeshTopology {
  /** Number of vertices. */
  n: number;
  /** index → Vertex (stable, mirrors `mesh.halfEdges.vertices` order). */
  order: Vertex[];
  /** Vertex → index. */
  indexOf: Map<Vertex, number>;
  /** CSR row pointers, length `n + 1`. */
  rowPtr: Int32Array;
  /** CSR neighbour column indices, length `nnz`. */
  colIdx: Int32Array;
  /** CSR cotangent weights `w_ij ≥ 0`, aligned with `colIdx`. */
  weight: Float64Array;
  /** Diagonal `L_ii = Σ_j w_ij`, length `n`. */
  diag: Float64Array;
  /** Rest positions in mesh-local coords, length `3n` (x,y,z interleaved). */
  restPos: Float64Array;
}

export interface CotangentLaplacianOptions {
  /** Lower clamp on |sin θ| when forming cot θ (guards degenerate angles). */
  minSin?: number;
  /** Upper clamp on |cot θ|. */
  maxCot?: number;
  /** Triangles with area below this are skipped (their cotangents are meaningless). */
  minArea?: number;
  /**
   * Clamp final per-edge weights to ≥ 0 (default true). Obtuse triangles produce
   * negative cotangent weights, which make the per-vertex ARAP covariance indefinite
   * — then the local rotation fit no longer recovers a rigid motion and ARAP drifts.
   * Non-negative weights keep every covariance PSD (and `L` SPD), at a small accuracy
   * cost near obtuse triangles. Standard robustness measure for ARAP.
   */
  clampNonNegative?: boolean;
}

const DEFAULTS: Required<CotangentLaplacianOptions> = {
  minSin: 1e-6,
  maxCot: 1e4,
  minArea: 1e-12,
  clampNonNegative: true,
};

const _u = new Vector3();
const _v = new Vector3();
const _cross = new Vector3();

/** Cotangent of the angle at `p` in triangle (p, q, r), clamped for stability. */
function clampedCot(
  px: number, py: number, pz: number,
  qx: number, qy: number, qz: number,
  rx: number, ry: number, rz: number,
  minSin: number, maxCot: number,
): number {
  _u.set(qx - px, qy - py, qz - pz);
  _v.set(rx - px, ry - py, rz - pz);
  const dot = _u.dot(_v);
  _cross.crossVectors(_u, _v);
  const sin = Math.max(_cross.length(), minSin);
  const cot = dot / sin;
  return Math.max(-maxCot, Math.min(maxCot, cot));
}

/**
 * Builds the rest-pose cotangent Laplacian + CSR adjacency of `mesh`.
 *
 * Deterministic: vertices are indexed by `mesh.halfEdges.vertices` array order and
 * faces are visited in `mesh.halfEdges.faces` order, so the assembly never depends on
 * Map iteration order.
 *
 * @param restPositions per-vertex rest position (mesh-local). Missing vertices fall
 *   back to their current `vertex.position`.
 */
export function buildCotangentLaplacian(
  mesh: HalfEdgeMesh,
  restPositions: Map<Vertex, Vector3>,
  options: CotangentLaplacianOptions = {},
): MeshTopology {
  const { minSin, maxCot, minArea, clampNonNegative } = { ...DEFAULTS, ...options };

  const order = mesh.halfEdges.vertices.slice();
  const n = order.length;
  const indexOf = new Map<Vertex, number>();
  for (let i = 0; i < n; i++) indexOf.set(order[i], i);

  const restPos = new Float64Array(3 * n);
  for (let i = 0; i < n; i++) {
    const rp = restPositions.get(order[i]) ?? order[i].position;
    restPos[3 * i] = rp.x;
    restPos[3 * i + 1] = rp.y;
    restPos[3 * i + 2] = rp.z;
  }

  // Accumulate symmetric edge weights in a hash keyed by (lo * n + hi).
  const edgeWeight = new Map<number, number>();
  const addEdge = (a: number, b: number, w: number): void => {
    const lo = a < b ? a : b;
    const hi = a < b ? b : a;
    const key = lo * n + hi;
    edgeWeight.set(key, (edgeWeight.get(key) ?? 0) + w);
  };

  const px = (i: number) => restPos[3 * i];
  const py = (i: number) => restPos[3 * i + 1];
  const pz = (i: number) => restPos[3 * i + 2];

  for (const face of mesh.halfEdges.faces) {
    const verts = face.getVertices();
    if (verts.length !== 3) continue;
    const ia = indexOf.get(verts[0]);
    const ib = indexOf.get(verts[1]);
    const ic = indexOf.get(verts[2]);
    if (ia === undefined || ib === undefined || ic === undefined) continue;

    _u.set(px(ib) - px(ia), py(ib) - py(ia), pz(ib) - pz(ia));
    _v.set(px(ic) - px(ia), py(ic) - py(ia), pz(ic) - pz(ia));
    const area2 = _cross.crossVectors(_u, _v).length();
    if (area2 < minArea) continue; // degenerate triangle

    // cot of the angle at a vertex contributes ½cot to the OPPOSITE edge.
    const cotA = clampedCot(px(ia), py(ia), pz(ia), px(ib), py(ib), pz(ib), px(ic), py(ic), pz(ic), minSin, maxCot);
    const cotB = clampedCot(px(ib), py(ib), pz(ib), px(ic), py(ic), pz(ic), px(ia), py(ia), pz(ia), minSin, maxCot);
    const cotC = clampedCot(px(ic), py(ic), pz(ic), px(ia), py(ia), pz(ia), px(ib), py(ib), pz(ib), minSin, maxCot);
    addEdge(ib, ic, 0.5 * cotA);
    addEdge(ia, ic, 0.5 * cotB);
    addEdge(ia, ib, 0.5 * cotC);
  }

  // Build per-vertex neighbour lists from the edge hash (symmetric).
  const neighbors: number[][] = Array.from({ length: n }, () => []);
  const weights: number[][] = Array.from({ length: n }, () => []);
  for (const [key, raw] of edgeWeight) {
    const w = clampNonNegative ? Math.max(0, raw) : raw;
    const lo = Math.floor(key / n);
    const hi = key - lo * n;
    neighbors[lo].push(hi);
    weights[lo].push(w);
    neighbors[hi].push(lo);
    weights[hi].push(w);
  }

  // Flatten to CSR + diagonal.
  let nnz = 0;
  for (let i = 0; i < n; i++) nnz += neighbors[i].length;
  const rowPtr = new Int32Array(n + 1);
  const colIdx = new Int32Array(nnz);
  const weight = new Float64Array(nnz);
  const diag = new Float64Array(n);
  let cursor = 0;
  for (let i = 0; i < n; i++) {
    rowPtr[i] = cursor;
    const nb = neighbors[i];
    const wt = weights[i];
    let d = 0;
    for (let k = 0; k < nb.length; k++) {
      colIdx[cursor] = nb[k];
      weight[cursor] = wt[k];
      d += wt[k];
      cursor++;
    }
    diag[i] = d;
  }
  rowPtr[n] = cursor;

  return { n, order, indexOf, rowPtr, colIdx, weight, diag, restPos };
}
